import { KnockoutType, getKnockoutType } from '@ps/web-poker-tile-mappers';
import { Translation } from '@ps/web-shared-components';
import type { Currency, Speed } from '@ps/web-shared-generated-graphql/MTT';

import BritishPoundIcon from '../../assets/icons/currencies/british-pound.svg';
import CanadianDollarIcon from '../../assets/icons/currencies/canadian-dollar.svg';
import DollarIcon from '../../assets/icons/currencies/dollar.svg';
import EuroIcon from '../../assets/icons/currencies/euro.svg';
import ProgressiveKOIcon from '../../assets/icons/game-type/progressive-ko.svg';
import TotalKOIcon from '../../assets/icons/game-type/total-ko.svg';
import ZoomIcon from '../../assets/icons/game-type/zoom.svg';
import HyperTurboIcon from '../../assets/icons/speed/hyper-turbo.svg';
import RegularIcon from '../../assets/icons/speed/regular.svg';
import SlowIcon from '../../assets/icons/speed/slow.svg';
import TurboIcon from '../../assets/icons/speed/turbo.svg';
import Size10Icon from '../../assets/icons/table-size/seat-10.svg';
import Size2Icon from '../../assets/icons/table-size/seat-2.svg';
import Size3Icon from '../../assets/icons/table-size/seat-3.svg';
import Size4Icon from '../../assets/icons/table-size/seat-4.svg';
import Size5Icon from '../../assets/icons/table-size/seat-5.svg';
import Size6Icon from '../../assets/icons/table-size/seat-6.svg';
import Size7Icon from '../../assets/icons/table-size/seat-7.svg';
import Size8Icon from '../../assets/icons/table-size/seat-8.svg';
import Size9Icon from '../../assets/icons/table-size/seat-9.svg';

import {
  TXTCLI_CurrTitle_CAD,
  TXTCLI_CurrTitle_EUR,
  TXTCLI_CurrTitle_GBP,
  TXTCLI_CurrTitle_USD,
  TXTCLI_Maximum_N0_players_per_table,
  TXTCLI_SpeedX_Hyper_Turbo,
  TXTCLI_SpeedX_Regular,
  TXTCLI_SpeedX_Slow,
  TXTCLI_SpeedX_Turbo,
  TXTCLI_This_is_a_Progressive_Knockout_ELL,
  TXTCLI_This_is_a_Total_Progressive_Kn_ELL,
  TXTCLI_This_is_a_Zoom_TournamentX
} from './utils.messages.json';

interface IconItemConfig {
  Icon: React.ReactNode;
  TooltipContent: React.ReactNode;
}

const SEATS_ICON_CONFIG: Record<number, IconItemConfig> = {
  2: {
    Icon: <Size2Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 2 }} />
    )
  },
  3: {
    Icon: <Size3Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 3 }} />
    )
  },
  4: {
    Icon: <Size4Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 4 }} />
    )
  },
  5: {
    Icon: <Size5Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 5 }} />
    )
  },
  6: {
    Icon: <Size6Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 6 }} />
    )
  },
  7: {
    Icon: <Size7Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 7 }} />
    )
  },
  8: {
    Icon: <Size8Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 8 }} />
    )
  },
  9: {
    Icon: <Size9Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 9 }} />
    )
  },
  10: {
    Icon: <Size10Icon />,
    TooltipContent: (
      <Translation tagName="p" {...TXTCLI_Maximum_N0_players_per_table} values={{ 0: 10 }} />
    )
  }
};

const KNOCKOUT_ICON_CONFIG: Record<KnockoutType, IconItemConfig> = {
  [KnockoutType.KO]: {
    Icon: <ProgressiveKOIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_This_is_a_Progressive_Knockout_ELL} />
  },
  [KnockoutType.PKO]: {
    Icon: <ProgressiveKOIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_This_is_a_Progressive_Knockout_ELL} />
  },
  [KnockoutType.Ultra_PKO]: {
    Icon: <ProgressiveKOIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_This_is_a_Progressive_Knockout_ELL} />
  },
  [KnockoutType.Total_PKO]: {
    Icon: <TotalKOIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_This_is_a_Total_Progressive_Kn_ELL} />
  }
};

const SPEED_ICON_CONFIG: Record<Speed, IconItemConfig> = {
  SLOW: {
    Icon: <SlowIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_SpeedX_Slow} />
  },
  REGULAR: {
    Icon: <RegularIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_SpeedX_Regular} />
  },
  TURBO: {
    Icon: <TurboIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_SpeedX_Turbo} />
  },
  HYPER: {
    Icon: <HyperTurboIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_SpeedX_Hyper_Turbo} />
  }
};

const CURRENCY_ICON_CONFIG: Record<'CAD' | 'EUR' | 'GBP' | 'USD', IconItemConfig> = {
  CAD: {
    Icon: <CanadianDollarIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_CurrTitle_CAD} />
  },
  EUR: {
    Icon: <EuroIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_CurrTitle_EUR} />
  },
  GBP: {
    Icon: <BritishPoundIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_CurrTitle_GBP} />
  },
  USD: {
    Icon: <DollarIcon />,
    TooltipContent: <Translation tagName="p" {...TXTCLI_CurrTitle_USD} />
  }
};

export const getSeatsIcon = (seats: number): IconItemConfig => SEATS_ICON_CONFIG[seats];

export const getKnockoutIcon = (buyIn: number, bounty: number): IconItemConfig => {
  const knockoutType = getKnockoutType(buyIn, bounty);

  return KNOCKOUT_ICON_CONFIG[knockoutType];
};

export const getZoomIcon = (): IconItemConfig => ({
  Icon: <ZoomIcon />,
  TooltipContent: <Translation tagName="p" {...TXTCLI_This_is_a_Zoom_TournamentX} />
});

export const getSpeedIcon = (speed: Speed): IconItemConfig => SPEED_ICON_CONFIG[speed];

export const getCurrencyIcon = (currency: Currency): IconItemConfig => {
  if (currency === 'CAD' || currency === 'EUR' || currency === 'GBP' || currency === 'USD') {
    return CURRENCY_ICON_CONFIG[currency];
  }

  return CURRENCY_ICON_CONFIG.USD;
};
